<template>
    <div class="details">
        <div class="header d-flex justify-content-between align-items-start">
            <div class="title">
                <small class="label">{{ $t('[[[Temat]]]') }}</small>
                <h4>{{ chatDetails.name }}</h4>
            </div>
            <action-button :icon="`fa-solid ${editMode ? 'fa-check' : 'fa-pen'}`" @click="editMode = !editMode" :text="editMode ? $t('[[[Gotowe]]]') : $t('[[[Zarządzaj]]]')" />
        </div>
        <h5 class="section-title">
            <i class="fa fa-user" /> {{ $t('[[[Uczestnicy rozmowy:]]]') }}
            <span class="count">{{ chatDetails.roomUsers?.length || 0 }}</span>
        </h5>
        <ideo-form-group :label="$t('[[[Dodaj użytkownika]]]')" class="mt-3" v-if="editMode">
            <ideo-autocomplete
                v-model="newRoomUserPublicId"
                @update:modelValue="(publicId) => addUser(publicId)"
                track-by="publicId"
                :placeholder="$t('[[[Zacznij pisać aby wyszukać użytkownika...]]]')"
                :fetch="(publicId) => fetchUser(publicId)"
                :search="(query, limit) => searchUser(query, limit)"
                :disabled="!canManage"
            >
                <template #selected="{ option }">
                    <div class="d-flex flex-nowrap align-items-center">
                        <b class="ml-2">{{ option.givenName }} {{ option.surname }}</b>
                        <i class="ml-auto">{{ option.citiesNames }}</i>
                    </div>
                </template>
                <template #option="{ option }">
                    <div class="d-flex flex-nowrap align-items-center">
                        <b class="ml-2">{{ option.givenName }} {{ option.surname }}</b>
                        <i class="ml-auto">{{ option.citiesNames }}</i>
                    </div>
                </template>
            </ideo-autocomplete>
        </ideo-form-group>
        <ul class="participants">
            <li v-for="(roomUser, index) in chatDetails.roomUsers" :key="index" class="participant">
                <span class="avatar">{{ initials(roomUser) }}</span>
                <div class="info">
                    <span class="name">{{ roomUser.givenName }} {{ roomUser.surname }}</span>
                    <div v-if="!editMode" class="departments">
                        <span v-for="department in visibleDepartments(roomUser)" :key="department" class="dept">{{ department }}</span>
                        <button v-if="hiddenDepartmentsCount(roomUser) > 0" type="button" class="dept more" @click="toggleDepartments(roomUser)">
                            {{ isExpanded(roomUser) ? $t('[[[mniej]]]') : `+${hiddenDepartmentsCount(roomUser)}` }}
                        </button>
                        <span v-if="!userDepartments(roomUser.departments).length" class="text-muted">-</span>
                    </div>
                </div>
                <action-button v-if="editMode" class="ml-auto" variant="none" icon="fa-solid fa-trash" :text="$t('[[[Usuń]]]')" @click="remove(roomUser)" :confirm="$t('[[[Czy jesteś pewien? ]]]')" :disabled="!canDelete(roomUser) && !canManage" />
            </li>
        </ul>
        <template v-if="!editMode">
            <h5 class="section-title" v-if="chatDetails.reservationBookingCode || chatDetails.taskTitle || chatDetails.propertyName">
                <i class="fa fa-link" /> {{ $t('[[[Powiązane]]]') }}
            </h5>
            <p class="related" v-if="chatDetails.reservationBookingCode">
                <span class="label">{{ $t('[[[Rezerwacja]]]') }}</span> <router-link :to="{ name: 'renter-reservations-details', params: { publicId: chatDetails.reservationPublicId } }">{{ chatDetails.reservationBookingCode }}</router-link>
            </p>
            <p class="related" v-if="chatDetails.taskTitle">
                <span class="label">{{ $t('[[[Zadanie]]]') }}</span> <router-link :to="{ name: 'renter-tasks-edit', params: { publicId: chatDetails.taskPublicId } }">{{ chatDetails.taskTitle }}</router-link>
            </p>
            <p class="related" v-if="chatDetails.propertyName">
                <span class="label">{{ $t('[[[Nieruchomość]]]') }}</span> <router-link :to="{ name: 'renter-properties-info-item', params: { publicId: chatDetails.propertyPublicId } }">{{ chatDetails.propertyName }}</router-link>
            </p>
        </template>
    </div>
</template>
<script lang="ts">
import { Prop } from '@/helpers/Decorators';
import { DepartmentEnum, DepartmentName } from '@/modules/core/users/services/UsersService';
import { Vue, Options } from 'vue-class-component';
import { ChatRoomModel, ChatRoomUserModel } from '@/modules/renter/chat/services/ChatRoomService';
import ChatService from '@/modules/renter/chat/services/ChatService';
import RenterAssignUser from '@/modules/renter/components/RenterAssignUser.vue';
import { fetchUser, searchUser } from '@/modules/renter/common/helpers/autocomplete';
import { Resource } from '@/helpers/Interfaces';

@Options({
    components: { RenterAssignUser },
    emits: ['onUpdate']
})
export default class ChatDetails extends Vue
{
    @Prop() public chatDetails: ChatRoomModel;
    @Prop({ default: null }) public chatPublicId: string;

    public editMode: boolean = false;
    public newRoomUserPublicId: string = null;
    public canManage = false;
    private chatRoom: Resource<ChatRoomModel> = null;

    public async mounted(): Promise<void>
    {
        await this.loadChatRoom();
    }

    private async loadChatRoom(): Promise<boolean>
    {
        try
        {
            this.chatRoom = await ChatService.fetchRoom(this.chatRoomPublicId);

            this.canManage = this.chatRoom.meta.canManage;
        }
        catch (ex)
        {
            this.$log.debug(ex);

            return;
        }
    }

    private get chatRoomPublicId(): string
    {
        if (this.$route.params.public == "null") return '';

        return String(this.chatPublicId || this.$route.params.publicId || '');
    }

    // Ile ról pokazać przy uczestniku - reszta trafia do licznika "+N" i tooltipa.
    public maxDepartments = 3;

    public userDepartments(departments: DepartmentEnum[]): string[]
    {
        return (departments || []).map(department => DepartmentName(DepartmentEnum[department]));
    }

    // Uczestnicy, ktorym rozwinieto pelna liste rol (klucz - publicId uzytkownika).
    public expandedUsers: Record<string, boolean> = {};

    public isExpanded(user: ChatRoomUserModel): boolean
    {
        return !!this.expandedUsers[user.publicId];
    }

    public toggleDepartments(user: ChatRoomUserModel): void
    {
        this.expandedUsers = { ...this.expandedUsers, [user.publicId]: !this.isExpanded(user) };
    }

    public visibleDepartments(user: ChatRoomUserModel): string[]
    {
        const departments = this.userDepartments(user.departments);

        return this.isExpanded(user) ? departments : departments.slice(0, this.maxDepartments);
    }

    public hiddenDepartmentsCount(user: ChatRoomUserModel): number
    {
        return Math.max(this.userDepartments(user.departments).length - this.maxDepartments, 0);
    }

    public initials(user: ChatRoomUserModel): string
    {
        return `${(user.givenName || '').charAt(0)}${(user.surname || '').charAt(0)}`.toUpperCase() || '?';
    }

    public canDelete(roomUser: ChatRoomUserModel): boolean
    {
        return (roomUser.id === this.$auth.user()?.id || this.$auth.user()?.id === this.chatDetails.createdBy);
    }

    public async remove(user: ChatRoomUserModel): Promise<void>
    {
        try
        {
            const response = await ChatService.removeUserFromChatRoom(this.chatDetails.publicId, user.publicId);

            this.$alert.success(this.$t('[[[Usunięto użytkownika z czatu]]]'));
            this.$emit('onUpdate', response);
        }
        catch (ex)
        {
            this.$log.debug(ex);
            this.$alert.error(this.$t('[[[Nie udało się usunąć użytkownika z czatu]]]'));
        }
    }

    public async addUser(userPublicId: string): Promise<void>
    {
        if (!this.newRoomUserPublicId) return;

        await this.$nextTick();

        this.newRoomUserPublicId = null;

        const userAlreadyAssigned = this.chatDetails.roomUsers.find(({ publicId }) => userPublicId === publicId);

        if (userAlreadyAssigned)
        {
            this.$alert.error(this.$t('[[[Ten użytkownik już jest przypisany do czatu]]]'));

            return;
        }

        try
        {
            const response = await ChatService.addChatRoomUser(this.chatDetails.publicId, userPublicId);

            this.$alert.success(this.$t('[[[Dodano użytkownika do czatu]]]'));
            this.$emit('onUpdate', response);
        }
        catch (ex)
        {
            this.$log.debug(ex);
            this.$alert.error(this.$t('[[[Nie udało się dodać użytkownika do czatu]]]'));
        }
    }

    public fetchUser = fetchUser;
    public searchUser = searchUser;
}
</script>
<style lang="scss" scoped>
div.details{
    .label{
        color: var(--font-secondary-color);
        font-size: 0.75rem;
        text-transform: uppercase;
        letter-spacing: .04em;
    }
    .header{
        gap: 12px;
        margin-bottom: 8px;
        h4{ margin: 0; word-break: break-word; }
    }
    .section-title{
        display: flex;
        align-items: center;
        gap: 6px;
        margin: 18px 0 8px;
        padding-bottom: 6px;
        border-bottom: 1px solid var(--border-color);
        .count{
            background: var(--primary);
            color: #fff;
            border-radius: 10px;
            padding: 0 8px;
            font-size: 0.75rem;
            line-height: 1.5;
        }
    }
    ul.participants{
        list-style: none;
        margin: 0;
        padding: 0;
        max-height: 50vh;
        overflow-y: auto;
    }
    .participant{
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 8px 4px;
        border-bottom: 1px solid var(--border-color);
        &:last-child{ border-bottom: 0; }
        .avatar{
            flex: 0 0 36px;
            height: 36px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            background: var(--primary);
            color: #fff;
            font-weight: 600;
            font-size: 0.8rem;
        }
        .info{ min-width: 0; }
        .name{ font-weight: 600; }
        .departments{
            display: flex;
            flex-wrap: wrap;
            gap: 4px;
            margin-top: 2px;
        }
        .dept{
            background: var(--secondary);
            color: #151b1e;
            border-radius: 10px;
            padding: 0 8px;
            font-size: 0.7rem;
            line-height: 1.6;
            &.more{
                background: transparent;
                border: 1px solid var(--secondary);
                color: var(--font-secondary-color);
                cursor: pointer;
                &:hover{
                    border-color: var(--primary);
                    color: var(--primary);
                }
            }
        }
    }
    p.related{
        margin: 0 0 4px;
        .label{ display: inline-block; min-width: 90px; }
    }
}
</style>
